/* eslint-disable max-lines */
import React, {
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef
} from 'react';
import ReactPlayer from 'react-player';
import { AudioRefSources } from '@utils/types/editors/audio.ts';
import { OnProgressProps } from 'react-player/base';
import { PeaksInstance } from 'peaks.js';

interface AudioContextType {
  currentTime: number;
  timecodeOffset?: string;
  setTimecodeOffset: (offset: string) => void;
  setTime: (time: number, from?: AudioRefSources) => void;
  isPlaying: boolean;
  isMuted: boolean;
  mute: () => void;
  unMute: () => void;
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  ref: React.RefObject<ReactPlayer> | null;
  setRef: (ref: React.RefObject<ReactPlayer>) => void;
  pause: () => void;
  onMediaPause: () => void;
  rewind: (seconds?: number) => void;
  forward: (seconds?: number) => void;
  play: () => void;
  syncTime: (from: AudioRefSources) => void;
  onProgress: (state: OnProgressProps) => void;
  duration: number;
  setDuration: (duration: number) => void;
  initializePeaks: (instance: PeaksInstance) => void;
  peaksInstance: PeaksInstance | null;
  linkedPlayback: boolean;
  toggleLinkedPlayback: () => void;
}

const AudioContext = createContext<AudioContextType | undefined>(undefined);

interface AudioProviderProps {
  children: React.ReactNode;
  timecodeOffset?: string;
}

// useReducer types
type State = {
  currentTime: number;
  timecodeOffset: string;
  isPlaying: boolean;
  isMuted: boolean;
  playbackRate: number;
  ref: React.RefObject<ReactPlayer> | null;
  duration: number;
  peaksInstance: PeaksInstance | null;
};

type Action = {
  type:
    | 'SET_CURRENT_TIME'
    | 'SET_IS_PLAYING'
    | 'SET_IS_MUTED'
    | 'SET_PLAYBACK_RATE'
    | 'SET_REF'
    | 'SET_DURATION'
    | 'SET_TIMECODE_OFFSET'
    | 'SET_PEAKS_INSTANCE';
  payload:
    | null
    | number
    | boolean
    | string
    | React.RefObject<ReactPlayer>
    | PeaksInstance;
};

const initialState: State = {
  currentTime: 0,
  timecodeOffset: '0',
  isPlaying: false,
  isMuted: false,
  playbackRate: 1,
  ref: null,
  duration: 0,
  peaksInstance: null
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'SET_CURRENT_TIME':
      return {
        ...state,
        currentTime: Number(action.payload) || 0
      };
    case 'SET_IS_PLAYING':
      return {
        ...state,
        isPlaying: Boolean(action.payload)
      };
    case 'SET_IS_MUTED':
      return {
        ...state,
        isMuted: Boolean(action.payload)
      };
    case 'SET_PLAYBACK_RATE':
      return {
        ...state,
        playbackRate: Number(action.payload) || 1
      };
    case 'SET_REF':
      return {
        ...state,
        ref: action.payload as React.RefObject<ReactPlayer>
      };
    case 'SET_DURATION': {
      // HTMLMediaElement.duration is Infinity for streaming/unseekable media
      // (or media served without a Content-Length). A non-finite duration
      // crashes downstream Luxon formatters ("Invalid unit value Infinity"),
      // which takes the whole editor down via the error boundary. Clamp to 0.
      const nextDuration = Number(action.payload);
      return {
        ...state,
        duration: Number.isFinite(nextDuration) ? nextDuration : 0
      };
    }
    case 'SET_TIMECODE_OFFSET':
      return {
        ...state,
        timecodeOffset: action.payload as string
      };
    case 'SET_PEAKS_INSTANCE':
      return {
        ...state,
        peaksInstance: action.payload as PeaksInstance
      };
    default:
      return state;
  }
}

export function AudioProvider({
  children,
  timecodeOffset
}: AudioProviderProps) {
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    timecodeOffset: timecodeOffset || '0'
  });

  const [linkedPlayback, setLinkedPlayback] = React.useState(() => {
    try {
      const stored = localStorage.getItem('scriptix_linked_playback');
      return stored === null ? true : stored === 'true';
    } catch {
      // localStorage may be unavailable or quota exceeded
      return true;
    }
  });

  const toggleLinkedPlayback = useCallback(() => {
    setLinkedPlayback(prev => {
      const next = !prev;
      try {
        localStorage.setItem('scriptix_linked_playback', String(next));
      } catch {
        // Silently fail if localStorage quota is exceeded
      }
      return next;
    });
  }, []);

  // Ref to track playing state and prevent race conditions
  const playingRef = useRef(false);
  // Timestamp of last seek — used to ignore stale onProgress ticks
  const lastSeekRef = useRef(0);
  // Live refs — always current, immune to stale closures
  const playerRef = useRef<ReactPlayer | null>(null);
  const peaksRef = useRef<PeaksInstance | null>(null);

  // Keep live refs in sync with reducer state
  playerRef.current = state.ref?.current ?? null;
  peaksRef.current = state.peaksInstance;

  // Stop playback + release media on unmount (#2288). react-player's
  // FilePlayer.componentWillUnmount only runs `removeAttribute('src')`
  // and detaches listeners — it never calls `.pause()`. Chrome keeps
  // the orphaned <audio> element playing from the audio pipeline until
  // GC, so audio continues after we navigate back to the workspace.
  // Pause explicitly through the live ref before we tear down Peaks.
  useEffect(() => {
    return () => {
      try {
        const internal = playerRef.current?.getInternalPlayer?.();
        if (internal && typeof internal.pause === 'function') {
          internal.pause();
        }
      } catch {
        // Element already detached / paused — nothing to clean up.
      }
      if (peaksRef.current) {
        try {
          peaksRef.current.destroy();
        } catch {
          // peaks.destroy throws if already destroyed — safe to swallow.
        }
      }
    };
  }, []);

  useEffect(() => {
    if (timecodeOffset) {
      dispatch({ type: 'SET_TIMECODE_OFFSET', payload: timecodeOffset });
    }
  }, [timecodeOffset]);

  useEffect(() => {
    if (timecodeOffset && Number(timecodeOffset) > 0) {
      setTime(Number(timecodeOffset));
    }
  }, [timecodeOffset]);

  const setTimecodeOffset = useCallback((offset: string) => {
    dispatch({ type: 'SET_TIMECODE_OFFSET', payload: offset });
  }, []);

  const pause = () => {
    playingRef.current = false;
    const rpTime = playerRef.current?.getCurrentTime();
    setTime(rpTime || state.currentTime);
    dispatch({ type: 'SET_IS_PLAYING', payload: false });

    if (peaksRef.current) {
      peaksRef.current.player.pause();
    }
  };

  // Seek-aware pause for ReactPlayer's onPause callback.
  // Browsers may fire pause events on the media element during seekTo();
  // ignore those to keep playback running when clicking segments.
  const onMediaPause = useCallback(() => {
    if (Date.now() - lastSeekRef.current < 500) return;
    pause();
  }, []);

  const play = async () => {
    if (playingRef.current) return;
    playingRef.current = true;

    const actualTime = playerRef.current?.getCurrentTime() ?? state.currentTime;
    let refTime = actualTime;
    const diff = Math.abs(
      actualTime - (playerRef.current?.getDuration() || state.duration || 0)
    );
    if (diff <= 0.001) {
      refTime = Number(state.timecodeOffset) || 0;
    }

    setTime(refTime);
    dispatch({ type: 'SET_IS_PLAYING', payload: true });

    if (peaksRef.current) {
      try {
        await peaksRef.current.player.play();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          dispatch({ type: 'SET_IS_PLAYING', payload: false });
        }
      }
    }

    playingRef.current = false;
  };

  const rewind = (seconds?: number) => {
    const newTime = Math.max(state.currentTime - (seconds || 5), 0);
    setTime(newTime);
  };

  const forward = (seconds?: number) => {
    const newTime = Math.min(
      state.currentTime + (seconds || 5),
      playerRef.current?.getDuration() || state.duration || 0
    );
    setTime(newTime);
  };

  const setTime = (time: number, from: AudioRefSources = 'ref') => {
    const minTime = Number(state.timecodeOffset) || 0;
    const clampedTime = Math.max(time, minTime);

    // Use live refs — immune to stale closures
    if (playerRef.current) {
      playerRef.current.seekTo(clampedTime, 'seconds');
    }
    if (from !== 'peaks' && peaksRef.current) {
      peaksRef.current.player.seek(clampedTime);
    }

    lastSeekRef.current = Date.now();
    dispatch({ type: 'SET_CURRENT_TIME', payload: clampedTime });
  };

  const mute = () => {
    dispatch({ type: 'SET_IS_MUTED', payload: true });
  };

  const unMute = () => {
    dispatch({ type: 'SET_IS_MUTED', payload: false });
  };

  const setPlaybackRate = (rate: number) => {
    if (rate > 2 || rate < 0.5) return;
    dispatch({ type: 'SET_PLAYBACK_RATE', payload: rate });
  };

  const setRef = useCallback((ref: React.RefObject<ReactPlayer>) => {
    dispatch({ type: 'SET_REF', payload: ref });
  }, []);

  const syncTime = (from: AudioRefSources) => {
    switch (from) {
      case 'ref':
        if (state.ref?.current?.getCurrentTime()) {
          setTime(state.ref?.current?.getCurrentTime() || 0);
        }
        break;
      default:
        break;
    }
  };

  const onProgress = (progressState: OnProgressProps) => {
    if (!progressState.played) return;
    if (Date.now() - lastSeekRef.current < 500) return;
    if (progressState.playedSeconds) {
      dispatch({
        type: 'SET_CURRENT_TIME',
        payload: progressState.playedSeconds
      });
    }
  };

  const setDuration = (duration: number) => {
    dispatch({ type: 'SET_DURATION', payload: duration });
  };

  const initializePeaks = (instance: PeaksInstance) => {
    // Regenerate-audio-timeline re-inits peaks with the new versioned URL
    // while the old instance is still alive: without this, peaks.js would
    // append a second canvas into the overview container and both would
    // render on top of each other. Also releases the old media_url the
    // previous instance was pinning.
    if (peaksRef.current && peaksRef.current !== instance) {
      try {
        peaksRef.current.destroy();
      } catch {
        // peaks.destroy throws if already destroyed — safe to swallow.
      }
    }
    dispatch({ type: 'SET_PEAKS_INSTANCE', payload: instance });
  };

  return (
    <AudioContext.Provider
      value={{
        currentTime: state.currentTime,
        timecodeOffset: state.timecodeOffset,
        setTimecodeOffset,
        isPlaying: state.isPlaying,
        isMuted: state.isMuted,
        playbackRate: state.playbackRate,
        duration: state.duration,
        ref: state.ref,
        peaksInstance: state.peaksInstance,
        setTime,
        setPlaybackRate,
        setRef,
        pause,
        onMediaPause,
        rewind,
        forward,
        mute,
        unMute,
        play,
        syncTime,
        onProgress,
        setDuration,
        initializePeaks,
        linkedPlayback,
        toggleLinkedPlayback
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = React.useContext(AudioContext);
  if (context === undefined) {
    throw new Error('useAudio must be used within a AudioProvider');
  }
  return context;
}
